import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Card, CardContent, Typography, Button, TextField, Stack, Chip,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  IconButton, Dialog, DialogTitle, DialogContent, DialogActions,
  FormControl, InputLabel, Select, MenuItem, FormControlLabel, Switch,
  Grid, Alert, LinearProgress, Tooltip, Tabs, Tab, Checkbox,
  CircularProgress, List, ListItem, ListItemIcon, ListItemText, Divider,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Shield as ShieldIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Search as SearchIcon,
  CalendarMonth as CalendarMonthIcon,
} from '@mui/icons-material';
import { focusWindowsApi, calendarApi } from '../api/services';
import type { FocusWindow, CalendarEvent } from '../types/models';

const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'];
const HOURS = Array.from({ length: 13 }, (_, i) => i + 7); // 7am to 7pm

function timeToHour(time: string): number {
  const [h, m] = time.split(':').map(Number);
  return h + m / 60;
}

function formatHour(hour: number): string {
  const h = Math.floor(hour);
  const ampm = h >= 12 ? 'PM' : 'AM';
  const display = h > 12 ? h - 12 : h === 0 ? 12 : h;
  return `${display} ${ampm}`;
}

const BLOCK_COLOURS = [
  'rgba(108, 155, 255, 0.35)',
  'rgba(129, 199, 132, 0.35)',
  'rgba(255, 183, 77, 0.35)',
  'rgba(206, 147, 216, 0.35)',
  'rgba(77, 208, 225, 0.35)',
];

const FOCUS_PATTERNS = [
  /focus/i,
  /\bblock(ed)?\b/i,
  /deep\s*work/i,
  /no\s*meetings/i,
  /\bDND\b/,
  /do\s*not\s*disturb/i,
  /\bhold\b/i,
  /\bprotected\b/i,
];

const GENERIC_TITLES = ['blocked', 'hold', 'block', 'dnd'];

interface DetectedFocusBlock {
  dayOfWeek: string;
  startTime: string;
  endTime: string;
  label: string;
  sourceTitle: string;
  selected: boolean;
}

function getDayOfWeekName(date: Date): string {
  const names = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  return names[date.getDay()];
}

function getMonday(d: Date): Date {
  const date = new Date(d);
  const day = date.getDay();
  const diff = date.getDate() - day + (day === 0 ? -6 : 1);
  date.setDate(diff);
  date.setHours(0, 0, 0, 0);
  return date;
}

function formatDateOnly(d: Date): string {
  return d.toISOString().split('T')[0];
}

function extractTimeFromISO(isoStr: string): string {
  const d = new Date(isoStr);
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

function matchesFocusPattern(title: string): boolean {
  return FOCUS_PATTERNS.some(p => p.test(title));
}

function deriveFocusLabel(title: string): string {
  if (GENERIC_TITLES.some(g => title.toLowerCase().trim() === g)) {
    return 'Focus Time';
  }
  return title;
}

export default function FocusTimePage() {
  const [windows, setWindows] = useState<FocusWindow[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editItem, setEditItem] = useState<Partial<FocusWindow> | null>(null);

  const loadWindows = async () => {
    setLoading(true);
    try {
      setWindows(await focusWindowsApi.getAll());
    } catch (err) {
      console.error('Failed to load focus windows', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadWindows(); }, []);

  const handleSave = async () => {
    if (!editItem?.dayOfWeek || !editItem?.startTime || !editItem?.endTime) return;
    try {
      if (editItem.id) {
        await focusWindowsApi.update(editItem.id, editItem);
      } else {
        await focusWindowsApi.create(editItem);
      }
      setDialogOpen(false);
      setEditItem(null);
      loadWindows();
    } catch (err) {
      console.error('Failed to save focus window', err);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Delete this focus window?')) return;
    try {
      await focusWindowsApi.delete(id);
      loadWindows();
    } catch (err) {
      console.error('Failed to delete', err);
    }
  };

  // --- Add Focus Time dialog state ---
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [addTab, setAddTab] = useState(0);
  const [calendarEvents, setCalendarEvents] = useState<CalendarEvent[]>([]);
  const [calendarLoading, setCalendarLoading] = useState(false);
  const [calendarError, setCalendarError] = useState<string | null>(null);
  const [detectedBlocks, setDetectedBlocks] = useState<DetectedFocusBlock[]>([]);
  const [selectableEvents, setSelectableEvents] = useState<(CalendarEvent & { selected: boolean })[]>([]);
  const [creatingFromCalendar, setCreatingFromCalendar] = useState(false);

  const loadCalendarEvents = useCallback(async () => {
    setCalendarLoading(true);
    setCalendarError(null);
    try {
      const monday = getMonday(new Date());
      const sunday = new Date(monday);
      sunday.setDate(monday.getDate() + 6); // Full Mon-Sun week
      const events = await calendarApi.getByRange(formatDateOnly(monday), formatDateOnly(sunday));
      setCalendarEvents(events);
      return events;
    } catch (err) {
      console.error('Failed to load calendar events', err);
      setCalendarError('Failed to load calendar events. Please ensure a calendar source is connected.');
      return [];
    } finally {
      setCalendarLoading(false);
    }
  }, []);

  const runAutoDetect = useCallback(async () => {
    const events = await loadCalendarEvents();
    const nonAllDay = events.filter(e => !e.isAllDay);
    const blocks: DetectedFocusBlock[] = [];

    // 1. Events matching focus patterns
    const matched = nonAllDay.filter(e => matchesFocusPattern(e.title));
    for (const e of matched) {
      const startDate = new Date(e.startTime);
      blocks.push({
        dayOfWeek: getDayOfWeekName(startDate),
        startTime: extractTimeFromISO(e.startTime),
        endTime: extractTimeFromISO(e.endTime),
        label: deriveFocusLabel(e.title),
        sourceTitle: e.title,
        selected: true,
      });
    }

    // 2. Detect gaps between meetings (>= 45 min gaps as potential focus time)
    const WORK_START = 8; // 8 AM
    const WORK_END = 18;  // 6 PM
    const MIN_GAP_MINUTES = 45;
    const dayNames = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'];

    for (const dayName of dayNames) {
      const dayEvents = nonAllDay
        .filter(e => getDayOfWeekName(new Date(e.startTime)) === dayName)
        .sort((a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime());

      if (dayEvents.length === 0) continue;

      // Build time slots: work start → first meeting, between meetings, last meeting → work end
      const slots: { start: number; end: number }[] = [];
      const firstStart = new Date(dayEvents[0].startTime).getHours() + new Date(dayEvents[0].startTime).getMinutes() / 60;
      if (firstStart > WORK_START + MIN_GAP_MINUTES / 60) {
        slots.push({ start: WORK_START, end: firstStart });
      }

      for (let i = 0; i < dayEvents.length - 1; i++) {
        const gapStart = new Date(dayEvents[i].endTime).getHours() + new Date(dayEvents[i].endTime).getMinutes() / 60;
        const gapEnd = new Date(dayEvents[i + 1].startTime).getHours() + new Date(dayEvents[i + 1].startTime).getMinutes() / 60;
        if ((gapEnd - gapStart) * 60 >= MIN_GAP_MINUTES) {
          slots.push({ start: gapStart, end: gapEnd });
        }
      }

      const lastEnd = new Date(dayEvents[dayEvents.length - 1].endTime).getHours() + new Date(dayEvents[dayEvents.length - 1].endTime).getMinutes() / 60;
      if (WORK_END - lastEnd >= MIN_GAP_MINUTES / 60) {
        slots.push({ start: lastEnd, end: WORK_END });
      }

      for (const slot of slots) {
        const startH = Math.floor(slot.start);
        const startM = Math.round((slot.start - startH) * 60);
        const endH = Math.floor(slot.end);
        const endM = Math.round((slot.end - endH) * 60);
        const duration = Math.round((slot.end - slot.start) * 60);

        // Skip if this gap overlaps with an already-detected focus block on the same day
        const overlaps = blocks.some(b =>
          b.dayOfWeek === dayName &&
          b.startTime < `${String(endH).padStart(2,'0')}:${String(endM).padStart(2,'0')}` &&
          b.endTime > `${String(startH).padStart(2,'0')}:${String(startM).padStart(2,'0')}`
        );
        if (overlaps) continue;

        blocks.push({
          dayOfWeek: dayName,
          startTime: `${String(startH).padStart(2,'0')}:${String(startM).padStart(2,'0')}`,
          endTime: `${String(endH).padStart(2,'0')}:${String(endM).padStart(2,'0')}`,
          label: `Focus Time (${duration}m gap)`,
          sourceTitle: `Gap between meetings on ${dayName}`,
          selected: false, // gaps are unchecked by default — user opts in
        });
      }
    }

    setDetectedBlocks(blocks);
  }, [loadCalendarEvents]);

  const loadSelectableEvents = useCallback(async () => {
    const events = await loadCalendarEvents();
    const nonAllDay = events.filter(e => !e.isAllDay);
    setSelectableEvents(nonAllDay.map(e => ({ ...e, selected: false })));
  }, [loadCalendarEvents]);

  const handleOpenAddDialog = (tab: number) => {
    setAddTab(tab);
    setAddDialogOpen(true);
    setCalendarError(null);
    setDetectedBlocks([]);
    setSelectableEvents([]);
    if (tab === 0) {
      runAutoDetect();
    } else if (tab === 1) {
      loadSelectableEvents();
    }
  };

  const handleAddTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setAddTab(newValue);
    setCalendarError(null);
    if (newValue === 0 && detectedBlocks.length === 0) {
      runAutoDetect();
    } else if (newValue === 1 && selectableEvents.length === 0) {
      loadSelectableEvents();
    }
  };

  const handleCreateFromDetected = async () => {
    const selected = detectedBlocks.filter(b => b.selected);
    if (selected.length === 0) return;
    setCreatingFromCalendar(true);
    try {
      for (const block of selected) {
        await focusWindowsApi.create({
          dayOfWeek: block.dayOfWeek,
          startTime: block.startTime,
          endTime: block.endTime,
          label: block.label,
          isRecurring: true,
        });
      }
      setAddDialogOpen(false);
      loadWindows();
    } catch (err) {
      console.error('Failed to create focus windows', err);
    } finally {
      setCreatingFromCalendar(false);
    }
  };

  const handleCreateFromSelected = async () => {
    const selected = selectableEvents.filter(e => e.selected);
    if (selected.length === 0) return;
    setCreatingFromCalendar(true);
    try {
      for (const evt of selected) {
        const startDate = new Date(evt.startTime);
        await focusWindowsApi.create({
          dayOfWeek: getDayOfWeekName(startDate),
          startTime: extractTimeFromISO(evt.startTime),
          endTime: extractTimeFromISO(evt.endTime),
          label: deriveFocusLabel(evt.title),
          isRecurring: true,
        });
      }
      setAddDialogOpen(false);
      loadWindows();
    } catch (err) {
      console.error('Failed to create focus windows', err);
    } finally {
      setCreatingFromCalendar(false);
    }
  };

  const openNewDialog = () => {
    setEditItem({
      dayOfWeek: 'Monday',
      startTime: '09:00',
      endTime: '11:00',
      label: '',
      isRecurring: true,
    });
    setDialogOpen(true);
  };

  // Calculate total weekly focus hours
  const totalMinutes = windows.reduce((sum, w) => {
    const start = timeToHour(w.startTime);
    const end = timeToHour(w.endTime);
    return sum + (end - start) * 60;
  }, 0);
  const totalHours = Math.round(totalMinutes / 60 * 10) / 10;

  // Group windows by day for the heatmap
  const windowsByDay: Record<string, FocusWindow[]> = {};
  DAYS.forEach(d => { windowsByDay[d] = []; });
  windows.forEach(w => {
    if (windowsByDay[w.dayOfWeek]) {
      windowsByDay[w.dayOfWeek].push(w);
    }
  });

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="h4">Focus Time</Typography>
        <Stack direction="row" spacing={1}>
          <Button variant="outlined" startIcon={<SearchIcon />} onClick={() => handleOpenAddDialog(0)}>
            Auto-Detect from Calendar
          </Button>
          <Button variant="outlined" startIcon={<CalendarMonthIcon />} onClick={() => handleOpenAddDialog(1)}>
            Select from Calendar
          </Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={openNewDialog}>
            Add Manually
          </Button>
        </Stack>
      </Box>

      {/* Capacity Summary */}
      <Card sx={{ mb: 3 }}>
        <CardContent sx={{ display: 'flex', gap: 3, alignItems: 'center', flexWrap: 'wrap' }}>
          <Box>
            <Typography variant="caption" color="text.secondary">Weekly Focus Time</Typography>
            <Typography variant="h5">{totalHours}h</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">Focus Windows</Typography>
            <Typography variant="h5">{windows.length}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">Days Covered</Typography>
            <Typography variant="h5">
              {DAYS.filter(d => windowsByDay[d]?.length > 0).length} / 5
            </Typography>
          </Box>
          <Box sx={{ borderLeft: 1, borderColor: 'divider', pl: 3 }}>
            <Typography variant="caption" color="text.secondary">Focus vs Work Week</Typography>
            <Typography variant="h5">
              {totalHours > 0 ? Math.round((totalHours / 40) * 100) : 0}%
            </Typography>
            <Typography variant="caption" color="text.secondary">
              of 40h week
            </Typography>
          </Box>
        </CardContent>
      </Card>

      {/* Focus vs Meetings Ratio */}
      {(() => {
        const workWeekHours = 40;
        const focusPct = totalHours > 0 ? Math.round((totalHours / workWeekHours) * 100) : 0;
        const meetingEstPct = Math.max(0, Math.min(100 - focusPct, 100 - focusPct));
        const otherPct = Math.max(0, 100 - focusPct - meetingEstPct);
        return (
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Time Allocation Breakdown
              </Typography>
              <Box sx={{ display: 'flex', height: 8, borderRadius: 4, overflow: 'hidden', mb: 1.5 }}>
                <Box sx={{ width: `${focusPct}%`, bgcolor: 'rgba(105, 240, 174, 0.6)', transition: 'width 0.3s' }} />
                <Box sx={{ width: `${meetingEstPct}%`, bgcolor: 'rgba(108, 155, 255, 0.6)', transition: 'width 0.3s' }} />
                <Box sx={{ flex: 1, bgcolor: 'rgba(255,255,255,0.06)' }} />
              </Box>
              <Stack direction="row" spacing={3}>
                <Stack direction="row" spacing={0.75} alignItems="center">
                  <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: 'rgba(105, 240, 174, 0.6)' }} />
                  <Typography variant="caption" color="text.secondary">Focus: {focusPct}%</Typography>
                </Stack>
                <Stack direction="row" spacing={0.75} alignItems="center">
                  <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: 'rgba(108, 155, 255, 0.6)' }} />
                  <Typography variant="caption" color="text.secondary">Meetings: {meetingEstPct}%</Typography>
                </Stack>
                <Stack direction="row" spacing={0.75} alignItems="center">
                  <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.06)' }} />
                  <Typography variant="caption" color="text.secondary">Other: {otherPct}%</Typography>
                </Stack>
              </Stack>
            </CardContent>
          </Card>
        );
      })()}

      {/* Scheduling Respects Focus Indicator */}
      <Alert
        severity="info"
        icon={<ShieldIcon />}
        sx={{ mb: 3, bgcolor: 'rgba(108, 155, 255, 0.06)', border: '1px solid rgba(108, 155, 255, 0.15)' }}
      >
        <Typography variant="body2">
          <strong>Schedule respects focus.</strong> The scheduling engine treats your declared focus windows as protected time.
          Tasks will not be scheduled into focus windows unless no other time is available.
        </Typography>
      </Alert>

      <Grid container spacing={2.5}>
        {/* Weekly Heatmap */}
        <Grid size={{ xs: 12, lg: 8 }}>
          <Card>
            <CardContent>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Weekly Focus Grid
              </Typography>
              <Box sx={{ overflowX: 'auto' }}>
                <Box sx={{ display: 'grid', gridTemplateColumns: '60px repeat(5, 1fr)', gap: 0, minWidth: 500 }}>
                  {/* Header row */}
                  <Box />
                  {DAYS.map(day => (
                    <Box key={day} sx={{ textAlign: 'center', py: 1 }}>
                      <Typography variant="caption" fontWeight={600} color="text.secondary">
                        {day.substring(0, 3)}
                      </Typography>
                    </Box>
                  ))}

                  {/* Hour rows */}
                  {HOURS.map(hour => (
                    <React.Fragment key={hour}>
                      <Box sx={{ display: 'flex', alignItems: 'center', pr: 1, justifyContent: 'flex-end' }}>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                          {formatHour(hour)}
                        </Typography>
                      </Box>
                      {DAYS.map(day => {
                        const dayWindows = windowsByDay[day] || [];
                        const activeWindow = dayWindows.find(w => {
                          const start = timeToHour(w.startTime);
                          const end = timeToHour(w.endTime);
                          return hour >= start && hour < end;
                        });
                        const colourIndex = activeWindow
                          ? dayWindows.indexOf(activeWindow) % BLOCK_COLOURS.length
                          : -1;

                        return (
                          <Box
                            key={`${day}-${hour}`}
                            sx={{
                              height: 32,
                              bgcolor: activeWindow ? BLOCK_COLOURS[colourIndex] : 'rgba(255,255,255,0.02)',
                              border: 1,
                              borderColor: 'divider',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              transition: 'background-color 0.2s',
                            }}
                          >
                            {activeWindow && hour === Math.ceil(timeToHour(activeWindow.startTime)) && (
                              <Typography variant="caption" sx={{ fontSize: '0.65rem', fontWeight: 500, color: 'text.primary' }}>
                                {activeWindow.label}
                              </Typography>
                            )}
                          </Box>
                        );
                      })}
                    </React.Fragment>
                  ))}
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Focus Windows List */}
        <Grid size={{ xs: 12, lg: 4 }}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Declared Windows
              </Typography>
              {windows.length === 0 && !loading && (
                <Typography color="text.secondary" variant="body2" sx={{ py: 2 }}>
                  No focus windows declared yet
                </Typography>
              )}
              <Stack spacing={1}>
                {windows.map((w, i) => (
                  <Box
                    key={w.id}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      p: 1.5,
                      borderRadius: 1,
                      bgcolor: BLOCK_COLOURS[i % BLOCK_COLOURS.length],
                      borderLeft: 3,
                      borderColor: 'primary.main',
                    }}
                  >
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="body2" fontWeight={600}>{w.label || 'Focus Block'}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {w.dayOfWeek} {w.startTime.substring(0, 5)} - {w.endTime.substring(0, 5)}
                      </Typography>
                      {w.isRecurring && (
                        <Chip label="Recurring" size="small" variant="outlined" sx={{ ml: 1, height: 18, fontSize: '0.6rem' }} />
                      )}
                    </Box>
                    <IconButton size="small" onClick={() => { setEditItem(w); setDialogOpen(true); }}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small" onClick={() => handleDelete(w.id)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                ))}
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Manual Add/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editItem?.id ? 'Edit Focus Window' : 'New Focus Window'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Label"
              fullWidth
              value={editItem?.label || ''}
              onChange={e => setEditItem(prev => ({ ...prev, label: e.target.value }))}
              placeholder="e.g. Deep Work, Strategy, Writing"
            />
            <FormControl fullWidth>
              <InputLabel>Day of Week</InputLabel>
              <Select
                value={editItem?.dayOfWeek || 'Monday'}
                label="Day of Week"
                onChange={e => setEditItem(prev => ({ ...prev, dayOfWeek: e.target.value }))}
              >
                {DAYS.map(d => <MenuItem key={d} value={d}>{d}</MenuItem>)}
              </Select>
            </FormControl>
            <Stack direction="row" spacing={2}>
              <TextField
                label="Start Time"
                type="time"
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={editItem?.startTime || '09:00'}
                onChange={e => setEditItem(prev => ({ ...prev, startTime: e.target.value }))}
              />
              <TextField
                label="End Time"
                type="time"
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={editItem?.endTime || '11:00'}
                onChange={e => setEditItem(prev => ({ ...prev, endTime: e.target.value }))}
              />
            </Stack>
            <FormControlLabel
              control={
                <Switch
                  checked={editItem?.isRecurring ?? true}
                  onChange={e => setEditItem(prev => ({ ...prev, isRecurring: e.target.checked }))}
                />
              }
              label="Recurring weekly"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSave}>Save</Button>
        </DialogActions>
      </Dialog>

      {/* Add Focus Time from Calendar Dialog */}
      <Dialog
        open={addDialogOpen}
        onClose={() => setAddDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Add Focus Time</DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          <Tabs
            value={addTab}
            onChange={handleAddTabChange}
            sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}
          >
            <Tab label="Auto-Detect" />
            <Tab label="Select from Calendar" />
          </Tabs>

          <Box sx={{ p: 3, minHeight: 300 }}>
            {/* Tab 0: Auto-Detect */}
            {addTab === 0 && (
              <Box>
                {calendarLoading && (
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4, gap: 2 }}>
                    <CircularProgress size={32} />
                    <Typography variant="body2" color="text.secondary">
                      Scanning calendar for focus time patterns...
                    </Typography>
                  </Box>
                )}
                {calendarError && (
                  <Alert severity="warning" sx={{ mb: 2 }}>{calendarError}</Alert>
                )}
                {!calendarLoading && !calendarError && detectedBlocks.length === 0 && (
                  <Alert severity="info">
                    No focus time blocks detected in your calendar this week. Try adding events with
                    titles containing "Focus", "Deep Work", "No Meetings", "DND", "HOLD", or "Protected".
                  </Alert>
                )}
                {!calendarLoading && detectedBlocks.length > 0 && (
                  <>
                    <Alert severity="success" sx={{ mb: 2 }}>
                      Found {detectedBlocks.length} potential focus time block{detectedBlocks.length !== 1 ? 's' : ''} in your calendar.
                    </Alert>
                    <List disablePadding>
                      {detectedBlocks.map((block, idx) => (
                        <React.Fragment key={idx}>
                          {idx > 0 && <Divider />}
                          <ListItem
                            sx={{ px: 0, cursor: 'pointer' }}
                            onClick={() => {
                              setDetectedBlocks(prev =>
                                prev.map((b, i) => i === idx ? { ...b, selected: !b.selected } : b)
                              );
                            }}
                          >
                            <ListItemIcon sx={{ minWidth: 40 }}>
                              <Checkbox
                                edge="start"
                                checked={block.selected}
                                tabIndex={-1}
                                disableRipple
                              />
                            </ListItemIcon>
                            <ListItemText
                              primary={
                                <Stack direction="row" spacing={1} alignItems="center">
                                  <Typography variant="body2" fontWeight={600}>
                                    {block.dayOfWeek}
                                  </Typography>
                                  <Typography variant="body2" color="text.secondary">
                                    {block.startTime} - {block.endTime}
                                  </Typography>
                                </Stack>
                              }
                              secondary={
                                <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }}>
                                  <Chip label={block.label} size="small" color="primary" variant="outlined" />
                                  <Typography variant="caption" color="text.secondary">
                                    from: "{block.sourceTitle}"
                                  </Typography>
                                </Stack>
                              }
                            />
                          </ListItem>
                        </React.Fragment>
                      ))}
                    </List>
                  </>
                )}
              </Box>
            )}

            {/* Tab 1: Select from Calendar */}
            {addTab === 1 && (
              <Box>
                {calendarLoading && (
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4, gap: 2 }}>
                    <CircularProgress size={32} />
                    <Typography variant="body2" color="text.secondary">
                      Loading calendar events...
                    </Typography>
                  </Box>
                )}
                {calendarError && (
                  <Alert severity="warning" sx={{ mb: 2 }}>{calendarError}</Alert>
                )}
                {!calendarLoading && !calendarError && selectableEvents.length === 0 && (
                  <Alert severity="info">
                    No calendar events found for this week. Please ensure a calendar source is synced.
                  </Alert>
                )}
                {!calendarLoading && selectableEvents.length > 0 && (
                  <>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      Select events that represent focus time. They will be created as recurring weekly focus windows.
                    </Typography>
                    {DAYS.map(day => {
                      const dayEvents = selectableEvents.filter(e => {
                        const d = new Date(e.startTime);
                        return getDayOfWeekName(d) === day;
                      });
                      if (dayEvents.length === 0) return null;
                      return (
                        <Box key={day} sx={{ mb: 2 }}>
                          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>
                            {day}
                          </Typography>
                          <List disablePadding dense>
                            {dayEvents.map(evt => (
                              <ListItem
                                key={evt.id}
                                sx={{ px: 0, cursor: 'pointer' }}
                                onClick={() => {
                                  setSelectableEvents(prev =>
                                    prev.map(e => e.id === evt.id ? { ...e, selected: !e.selected } : e)
                                  );
                                }}
                              >
                                <ListItemIcon sx={{ minWidth: 40 }}>
                                  <Checkbox
                                    edge="start"
                                    checked={evt.selected}
                                    tabIndex={-1}
                                    disableRipple
                                  />
                                </ListItemIcon>
                                <ListItemText
                                  primary={evt.title}
                                  secondary={`${extractTimeFromISO(evt.startTime)} - ${extractTimeFromISO(evt.endTime)}`}
                                />
                                {matchesFocusPattern(evt.title) && (
                                  <Chip label="Looks like focus time" size="small" color="success" variant="outlined" sx={{ ml: 1 }} />
                                )}
                              </ListItem>
                            ))}
                          </List>
                          <Divider />
                        </Box>
                      );
                    })}
                  </>
                )}
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddDialogOpen(false)}>Cancel</Button>
          {addTab === 0 && (
            <Button
              variant="contained"
              onClick={handleCreateFromDetected}
              disabled={creatingFromCalendar || detectedBlocks.filter(b => b.selected).length === 0}
              startIcon={creatingFromCalendar ? <CircularProgress size={16} /> : undefined}
            >
              Create {detectedBlocks.filter(b => b.selected).length} Focus Window{detectedBlocks.filter(b => b.selected).length !== 1 ? 's' : ''}
            </Button>
          )}
          {addTab === 1 && (
            <Button
              variant="contained"
              onClick={handleCreateFromSelected}
              disabled={creatingFromCalendar || selectableEvents.filter(e => e.selected).length === 0}
              startIcon={creatingFromCalendar ? <CircularProgress size={16} /> : undefined}
            >
              Create {selectableEvents.filter(e => e.selected).length} Focus Window{selectableEvents.filter(e => e.selected).length !== 1 ? 's' : ''}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
}
